import React, {useState} from 'react'
import {useSelector} from 'react-redux'
import _ from 'lodash'
import {
    Box,
    Container,
    InputAdornment,
    Pagination,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    Tooltip,
    Typography
} from '@mui/material'
import FilterListIcon from '@mui/icons-material/FilterList'
import ClearIcon from '@mui/icons-material/Clear'
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined'
import {visuallyHidden} from '@mui/utils'

import {RESULT_TABLE_HEADERS} from 'features/HomePage/ViewSchedules/viewSchedules.constant'
import {stableSort} from 'features/HomePage/ViewSchedules/viewSchedules.utils'
import Binocular from 'assets/images/binocular.svg'
import {getSmallLogoByFlightNumber} from 'config/logo.constant'
import {type RootState} from 'redux/store'

type Order = 'asc' | 'desc'

const SortTable = () => {
    const recordsPerPage = 12
    const [filterValue, setFilterValue] = useState<string>('')
    const [order, setOrder] = useState<Order>('asc')
    const [orderBy, setOrderBy] = useState<string>('departTime')
    const results = useSelector((state: any) => state.viewSchedules.response)
    const carrier = useSelector((state: any) => state.viewSchedules.carrier)
    const error = useSelector((state: RootState) => state.viewSchedules.error)
    const smallLogos = useSelector((state: RootState) => state.login.smallLogos)
    const isLoading = useSelector((state: RootState) => state.viewSchedules.isLoading)
    const [sortedData, setSortedData] = useState(results)
    const [pageCount, setPageCount] = useState<number>(1)
    const [startIndex, setStartIndex] = useState<number>(1)
    const [endIndex, setEndIndex] = useState<number>(sortedData.length < 12 ? sortedData.length : 12)
    const [page, setPage] = useState<number>(1)

    const sortHandler = (id: string) => {
        setOrderBy((value) => {
            if (value === id) {
                setOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))
            } else {
                setOrder('asc')
            }
            return id
        })
    }

    const filterHandler = (e) => {
        setFilterValue(e.target.value.replace(/[^a-zA-Z0-9]/g, ''))
    }

    const getClassName = (length: number, index: number) => {
        if (length <= 1) {
            return ''
        } else if (index === 0) {
            return 'start-line'
        } else if (index === length - 1) {
            return 'end-line'
        } else {
            return 'connect-line'
        }
    }

    const paginationHandler = (event, pageNumber) => {
        let end = pageNumber * recordsPerPage
        if (sortedData.length < end) {
            end = sortedData.length
        }
        setStartIndex((pageNumber - 1) * recordsPerPage)
        setEndIndex(end)
        setPage(pageNumber)
        window.scrollTo({
            top: 300,
            behavior: 'smooth'
        })
    }

    React.useEffect(() => {
        let data: any = []
        data = stableSort(_.cloneDeep(results), order, orderBy)
        if (filterValue.length > 0) {
            data = data.filter((obj) => {
                const filterFunction = (d) => {
                    let value = false
                    d.forEach((item) => {
                        if (item.flight?.toLowerCase()?.includes(filterValue.toLowerCase())) {
                            value = true
                        }
                    })
                    return value
                }
                return filterFunction(obj)
            })
        }
        setSortedData(data)
    }, [order, orderBy, results, filterValue])

    React.useEffect(() => {
        setOrderBy('departTime')
        setOrder('asc')
        setFilterValue('')
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        })
    }, [results])

    React.useEffect(() => {
        const remainder = sortedData.length % recordsPerPage
        let count = (sortedData.length - remainder) / recordsPerPage
        if (remainder > 0) {
            count = count + 1
        }
        setPageCount(count === 0 ? 1 : count)
        let end = 1 * recordsPerPage
        if (sortedData.length < end) {
            end = sortedData.length
        }
        setStartIndex(0)
        setEndIndex(end)
        setPage(1)
    }, [sortedData])

    return (
        <div className='result-container'>
            {results?.length > 0 && (
                <TextField
                    name='filter'
                    className='filterFlight'
                    value={filterValue}
                    onChange={filterHandler}
                    disabled={isLoading}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position='start'>
                                <FilterListIcon className='filter-icon' />
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <InputAdornment position='end'>
                                {filterValue?.length > 0 && (
                                    <Tooltip title='Clear Filter'>
                                        <ClearIcon
                                            className='filter-icon clear-icon'
                                            onClick={() => !isLoading && setFilterValue('')}
                                        />
                                    </Tooltip>
                                )}
                            </InputAdornment>
                        )
                    }}
                    inputProps={{'data-testid': 'filtertextField'}}
                    placeholder='Filter by flight...'
                />
            )}
            <TableContainer component={Paper} className='sort-table-container'>
                {results?.length > 0 ? (
                    <>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    {RESULT_TABLE_HEADERS.map((headCell, index) => (
                                        <TableCell
                                            key={headCell.id}
                                            sortDirection={orderBy === headCell.id ? order : false}
                                        >
                                            {headCell.value.length > 0 && (
                                                <TableSortLabel
                                                    active={orderBy === headCell.id}
                                                    direction={orderBy === headCell.id ? order : 'asc'}
                                                    onClick={() => sortHandler(headCell.id)}
                                                >
                                                    {headCell.value}
                                                    {orderBy === headCell.id ? (
                                                        <Box component='span' sx={visuallyHidden}>
                                                            {order === 'desc'
                                                                ? 'sorted descending'
                                                                : 'sorted ascending'}
                                                        </Box>
                                                    ) : null}
                                                </TableSortLabel>
                                            )}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sortedData?.slice(startIndex, endIndex).map((rowData, index) =>
                                    rowData?.map((row, subIndex) => (
                                        <TableRow
                                            key={`row-${index}-${subIndex}`}
                                            className={index % 2 === 0 ? 'row-even' : 'row-odd'}
                                        >
                                            <TableCell key={`flight-${index}-${subIndex}`}>
                                                <div>
                                                    <div className={getClassName(rowData.length, subIndex)}>
                                                        <img
                                                            src={getSmallLogoByFlightNumber(
                                                                smallLogos,
                                                                row.flight,
                                                                carrier
                                                            )}
                                                            alt='logo'
                                                            className='logo'
                                                        />
                                                        <span>{row.flight}</span>
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell key={`depart-${index}-${subIndex}`}>
                                                {/* {row.status.toLowerCase() === 'late' ||
                                                    row.status.toLowerCase() === 'early'
                                                    ? <>
                                                        <span
                                                            className={row.status.toLowerCase() === 'late'
                                                                ? 'late-text'
                                                                : 'early-text'}
                                                        >
                                                            {row.departNewTime}
                                                        </span><br />
                                                        <span className='subtext'>
                                                            {`${row.departDate} - Sched. ${row.departTime}`}
                                                        </span><br />
                                                    </> : */}
                                                <span className='text'>
                                                    {`${row.departDisplayDate} - ${row.departTime}`}
                                                </span>
                                                <br />
                                                {/* } */}
                                                <span className='subtext'>{row.departure}</span>
                                            </TableCell>
                                            <TableCell key={`status-${index}-${subIndex}`}>
                                                <span>
                                                    <span className={row.status.toLowerCase().replace(/\s/g, '')}>
                                                        {row.status}
                                                    </span>
                                                </span>
                                            </TableCell>
                                            <TableCell key={`arrive-${index}-${subIndex}`}>
                                                {/* {row.status.toLowerCase() === 'late' ||
                                                    row.status.toLowerCase() === 'early'
                                                    ? <>
                                                        <span
                                                            className={row.status.toLowerCase() === 'late'
                                                                ? 'late-text'
                                                                : 'early-text'}
                                                        >
                                                            {row.arrivalNewTime}
                                                        </span><br />
                                                        <span className='subtext'>Sched. {row.arrivalTime}</span><br />
                                                    </>
                                                    : <><span className='text'>
                                                        {`${row.arrivalDisplayDate} - ${row.arrivalTime}`}
                                                    </span><br /></>
                                                } */}
                                                <span className='text'>
                                                    {`${row.arrivalDisplayDate} - ${row.arrivalTime}`}
                                                </span>
                                                <br />
                                                <span className='subtext'>{row.arrival}</span>
                                            </TableCell>
                                            <TableCell key={`aircraft-${index}-${subIndex}`}>{row.aircraft}</TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                        {!(sortedData?.length > 0) && (
                            <Container className='no-results-found'>
                                <Typography variant='h5'>No results found.</Typography>
                            </Container>
                        )}
                    </>
                ) : (
                    (error === null || error.length === 0) && (
                        <Container className='no-results-found'>
                            <Typography variant='h5'>No results found.</Typography>
                            <Typography variant='h6'>Please try another search.</Typography>
                            <img src={Binocular} alt='icon' />
                        </Container>
                    )
                )}
            </TableContainer>
            {sortedData.length > 12 && (
                <Container className='pagination'>
                    <Typography variant='h6'>
                        {`Showing items ${startIndex + 1} - ${endIndex} of ${sortedData.length}`}
                    </Typography>
                    <Pagination
                        count={pageCount}
                        color='primary'
                        onChange={paginationHandler}
                        page={page}
                        size='small'
                        siblingCount={1}
                    />
                </Container>
            )}
            {error !== null && error.length > 0 && (
                <Container className='error-results-found'>
                    <Typography variant='h5'>{`There was a system error (${error}).`}</Typography>
                    <Typography variant='h6'>Please try again later.</Typography>
                    <ErrorOutlineOutlinedIcon />
                </Container>
            )}
        </div>
    )
}

export default SortTable
